/**
 * AudioPlayer Component
 * Custom audio player with authentication for WAHA media
 */

import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Play, Pause, Loader2, AlertCircle } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { callWahaProxy } from '@/lib/services/wahaProxyService';

interface AudioPlayerProps {
  url: string;
  isVoiceNote?: boolean;
  avatarUrl?: string;
  avatarName?: string;
  timestamp?: string;
}

// Cache for audio blob URLs
const audioCache = new Map<string, string>();

// Velocità disponibili
const PLAYBACK_SPEEDS = [1, 1.5, 2] as const;
type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];

export const AudioPlayer = memo(function AudioPlayer({
  url,
  isVoiceNote = false,
  avatarUrl,
  avatarName,
  timestamp,
}: AudioPlayerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<number | null>(null);
  const { wahaConfig } = useWhatsAppChatStore();

  // Fetch audio with authentication
  const fetchAudio = useCallback(async () => {
    if (!url) {
      setLoading(false);
      setError(true);
      return;
    }

    // Check cache first
    const cached = audioCache.get(url);
    if (cached) {
      setBlobUrl(cached);
      setLoading(false);
      return;
    }

    // Data URL - use directly
    if (url.startsWith('data:')) {
      setBlobUrl(url);
      setLoading(false);
      return;
    }

    // Check if needs authentication (WAHA media URL)
    const isWahaUrl = url.includes('/api/files/') || url.includes('/api/media/') ||
      (wahaConfig?.apiUrl && url.startsWith(wahaConfig.apiUrl));

    try {
      let response: Response;
      if (isWahaUrl) {
        const urlObj = new URL(url);
        const path = urlObj.pathname + urlObj.search;
        response = await callWahaProxy({ method: 'GET', path });
      } else {
        response = await fetch(url);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      audioCache.set(url, objectUrl);
      setBlobUrl(objectUrl);
    } catch (err) {
      console.error('[AudioPlayer] Error fetching audio:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [url, wahaConfig]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setProgress(0);
    setDuration(0);
    setIsPlaying(false);
    setPlaybackSpeed(1);
    fetchAudio();
  }, [fetchAudio]);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Smooth progress update con requestAnimationFrame
  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    setProgress((audio.currentTime / duration) * 100);

    if (!audio.paused && !audio.ended) {
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, [duration]);

  // Start/stop animation loop quando cambia isPlaying
  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, updateProgress]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || error || loading) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((e) => {
        console.error('[AudioPlayer] Play error:', e);
        setError(true);
      });
    }
  }, [isPlaying, error, loading]);

  // Cicla tra le velocità di riproduzione
  const cycleSpeed = useCallback(() => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    const newSpeed = PLAYBACK_SPEEDS[nextIndex];

    setPlaybackSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  }, [playbackSpeed]);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setPlaybackSpeed(1);
    if (audioRef.current) {
      audioRef.current.playbackRate = 1;
    }
  }, []);

  const handleError = useCallback(() => {
    console.error('[AudioPlayer] Audio playback error:', url);
    setError(true);
  }, [url]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    audio.currentTime = clickPosition * duration;
    setProgress(clickPosition * 100);
  }, [duration]);

  // Avatar fallback (iniziali)
  const avatarInitials = avatarName
    ? avatarName.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    : '?';

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-3 min-w-[280px]">
        <div className="flex-shrink-0">
          <Loader2 className="w-4 h-4 text-[#37352F] dark:text-white animate-spin" />
        </div>
        <div className="flex-1 flex flex-col justify-center gap-0.5 pt-3.5">
          <div className="h-[3px] bg-[rgba(55,53,47,0.16)] dark:bg-white/20 rounded-full" />
          <div className="flex justify-between">
            <span className="text-[10px] text-[#787774]">--:--</span>
            {timestamp && <span className="text-[10px] text-[#787774]">{timestamp}</span>}
          </div>
        </div>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#37352F]/10 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-medium text-[#37352F] dark:text-white">{avatarInitials}</span>
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error || !blobUrl) {
    return (
      <div className="flex items-center gap-3 min-w-[280px]">
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        <span className="text-xs text-red-500 flex-1 pt-3.5">Audio non disponibile</span>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 opacity-50" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#37352F]/10 dark:bg-white/10 flex items-center justify-center flex-shrink-0 opacity-50">
            <span className="text-[10px] font-medium text-[#37352F] dark:text-white">{avatarInitials}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-[280px]">
      {/* Play/Pause button - nero, piccolo */}
      <button
        onClick={togglePlay}
        className="flex-shrink-0 text-[#37352F] dark:text-white hover:opacity-70 transition-opacity"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4" />
        )}
      </button>

      {/* Progress bar + duration + timestamp - centrato */}
      <div className="flex-1 flex flex-col justify-center gap-0.5 pt-3.5">
        <div
          className="h-[3px] bg-[rgba(55,53,47,0.16)] dark:bg-white/20 rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-[#2383E2] rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between">
          <span className="text-[10px] text-[#787774]">
            {isPlaying || progress > 0 ? formatTime((progress / 100) * duration) : formatTime(duration)}
          </span>
          {timestamp && <span className="text-[10px] text-[#787774]">{timestamp}</span>}
        </div>
      </div>

      {/* Avatar o Speed chip */}
      {isPlaying ? (
        <button
          onClick={cycleSpeed}
          className="flex-shrink-0 h-7 px-3 rounded-md bg-[#37352F]/10 dark:bg-white/10 hover:bg-[#37352F]/20 dark:hover:bg-white/20 transition-colors flex items-center justify-center"
        >
          <span className="text-[11px] font-bold text-[#37352F] dark:text-white">
            {playbackSpeed}x
          </span>
        </button>
      ) : avatarUrl ? (
        <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-[#37352F]/10 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-medium text-[#37352F] dark:text-white">{avatarInitials}</span>
        </div>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={blobUrl}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
      />
    </div>
  );
});

// Clear audio cache
export function clearAudioCache() {
  audioCache.forEach((blobUrl) => {
    URL.revokeObjectURL(blobUrl);
  });
  audioCache.clear();
}
